import React from "react";
import {
  Alert,
  Bank,
  Button,
  Flex,
  Input,
  LegacyCard,
  LegacyForm,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from "@payping/web-components";
import { CloseCircleOutlined, FiTrash } from "@payping/icons";
import { formatIBAN } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { getBankInfoFromSheba } from "@payping/utilities-shared/bank";
import { ValidSheba } from "@payping/utils/validation";
import { MerchantIbanStatusEnum } from "@payping/api/app/api/terminalmanagerTypes";
import type { FormListFieldData } from "@payping/web-components/legacyComponents/Form/types";
import styles from "../../../../assets/scss/profile/Profile.module.scss";

interface ShebaCardProps {
  iban?: string;
  status?: MerchantIbanStatusEnum;
  errorMessage?: string;
  onDelete?: () => void;
  onActivate?: (iban?: string) => void;
  disabled?: boolean;
  title?: string;
  isActiveIBAN?: boolean;
  isEditing?: boolean;
  fieldData: FormListFieldData;
  hasPending?: boolean;
}

interface EditCardContentProps {
  iban?: string;
  status?: MerchantIbanStatusEnum;
  hasPending?: boolean;
  onDelete?: () => void;
  isActiveIBAN?: boolean;
  onActivate?: (iban?: string) => void;
  fieldData: FormListFieldData;
}

interface DisplayCardContentProps {
  iban: string;
  status?: MerchantIbanStatusEnum;
  errorMessage?: string;
  title?: string;
  isActiveIBAN?: boolean;
  onActivate?: (iban?: string) => void;
}

const EditCardContent = ({
  iban,
  status,
  hasPending,
  onDelete,
  isActiveIBAN,
  onActivate,
  fieldData,
}: EditCardContentProps) => {
  const { key, name, ...restField } = fieldData;
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();

  return (
    <Flex vertical gap="middle" className="tw:w-full" key={key}>
      <Tooltip title={hasPending ? t("profile.bank.pendingRequestTooltip") : undefined}>
        <LegacyForm.Item
          {...restField}
          label={t("profile.bank.title")}
          name={[name, "title"]}
          rules={[{ required: true, message: t("profile.bank.titleRequired") }]}
        >
          <Input disabled={hasPending} placeholder={t("profile.bank.titlePlaceholder")} />
        </LegacyForm.Item>
      </Tooltip>
      <Tooltip title={hasPending ? t("profile.bank.pendingRequestTooltip") : undefined}>
        <LegacyForm.Item
          {...restField}
          label={t("profile.bank.ibanNumber")}
          name={[name, "iban"]}
          rules={[
            { required: true, message: t("profile.bank.ibanRequired") },
            {
              validator(_, value) {
                if (value && (value.startsWith("IR") ? !ValidSheba(value) : !ValidSheba(`IR${value}`))) {
                  return Promise.reject(new Error(t("shabaValidation")));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            onlyNumbers
            disabled={hasPending}
            className={styles.inputLtr}
            suffix="IR"
            placeholder={t("profile.bank.ibanPlaceholder")}
          />
        </LegacyForm.Item>
      </Tooltip>
      <Flex justify="flex-start" className="tw:w-full" gap={"small"}>
        {onActivate ? (
          <Tooltip
            title={status === MerchantIbanStatusEnum.Failed ? t("profile.bank.warnInvalidActiveIBAN") : undefined}
          >
            <Button
              onClick={() => {
                onActivate(iban);
              }}
              // disabled={isActiveIBAN}
              type={isActiveIBAN ? "primary" : "default"}
            >
              {isActiveIBAN ? t("profile.bank.activeIBAN") : t("profile.bank.chooseActiveIban")}
            </Button>
          </Tooltip>
        ) : null}
        {onDelete ? (
          <Tooltip
            title={
              isActiveIBAN
                ? t("profile.bank.warnDeleteActiveIBAN")
                : hasPending
                  ? t("profile.bank.pendingRequestTooltip")
                  : ""
            }
          >
            <Button
              disabled={hasPending}
              onClick={() => {
                if (isActiveIBAN) {
                  modal.confirm({
                    cancelText: t("cancel"),
                    content: t("profile.bank.warnDeleteActiveIBAN"),
                    mask: { closable: true },
                    okButtonProps: { type: "primary", danger: true },
                    okText: t("ok"),
                    onOk: async () => onDelete(),
                  });
                } else {
                  onDelete();
                }
              }}
              danger
              icon={<FiTrash width={16} height={16} />}
            >
              {t("common.delete")}
            </Button>
          </Tooltip>
        ) : null}
      </Flex>
      {contextHolder}
    </Flex>
  );
};

const DisplayCardContent = ({
  errorMessage,
  status,
  title,
  isActiveIBAN,
  iban,
  onActivate,
}: DisplayCardContentProps) => {
  const { t } = useAppTranslation();

  const statusInfo = (() => {
    switch (status) {
      case MerchantIbanStatusEnum.Init:
        return { color: "default", text: t("profile.bank.status.requested"), icon: null };
      case MerchantIbanStatusEnum.Pending:
        return { color: "warning", text: t("profile.bank.status.inProgress"), icon: null };
      case MerchantIbanStatusEnum.Failed:
        return { color: "error", text: t("profile.bank.status.rejected"), icon: null };
      case MerchantIbanStatusEnum.Accepted:
        return { color: "success", text: t("profile.bank.status.settlementReady"), icon: null };
      default:
        return { color: "default", text: t("profile.bank.status.unknown"), icon: null };
    }
  })();

  return (
    <>
      <Flex className="tw:w-full" align="center" justify="space-between">
        {title ? (
          <Typography.Text className="tw:text-gray-900 tw:text-sm tw:font-normal tw:leading-5">{title}</Typography.Text>
        ) : (
          <div className="tw:w-10"></div>
        )}
        {onActivate && !isActiveIBAN ? (
          <Popconfirm
            title={t("profile.bank.chooseActiveIban")}
            description={
              status === MerchantIbanStatusEnum.Failed
                ? t("profile.bank.warnInvalidActiveIBAN")
                : t("common.operationWarning")
            }
            onConfirm={() => {
              onActivate(iban);
            }}
          >
            <Button type={"default"}>{t("profile.bank.chooseActiveIban")}</Button>
          </Popconfirm>
        ) : null}
      </Flex>
      <Flex vertical gap="small" className="tw:w-full">
        <Flex wrap align="center" gap="small">
          <Typography.Text className="tw:text-gray-900 tw:text-xl tw:font-semibold tw:leading-7">
            {formatIBAN(iban)}
          </Typography.Text>
          {typeof status === "number" ? <Tag color={statusInfo.color}>{statusInfo.text}</Tag> : null}
          {isActiveIBAN ? <Tag color={"#87d068"}>{t("profile.bank.active")}</Tag> : null}
        </Flex>
        <Flex align="center" gap="small" className="tw:opacity-50">
          <Bank.Icon bankName={getBankInfoFromSheba(iban)?.name} width={16} />
          <Typography.Text className="tw:text-gray-900 tw:text-xs tw:font-normal tw:leading-5">
            {t("profile.bank.bankPrefix")} {getBankInfoFromSheba(iban)?.persianName}
          </Typography.Text>
        </Flex>
      </Flex>
      {status === MerchantIbanStatusEnum.Failed && errorMessage ? (
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-1">
          <div className="tw:flex tw:items-center tw:gap-1">
            <CloseCircleOutlined width={16} height={16} className="color--danger tw:flex-shrink-0" />
            <Typography.Text className="tw:text-gray-600 tw:text-xs! tw:leading-5">
              {t("profile.bank.errorAndDetails")}
            </Typography.Text>
          </div>
          <Typography.Text className="tw:text-gray-900 tw:text-right tw:text-xs! tw:leading-5">
            {errorMessage}
          </Typography.Text>
        </div>
      ) : null}
    </>
  );
};

const ShebaCard: React.FC<ShebaCardProps> = ({
  iban,
  status,
  errorMessage,
  onDelete,
  onActivate,
  title,
  isActiveIBAN = false,
  isEditing,
  fieldData,
  hasPending,
}) => {
  const { t } = useAppTranslation();

  return iban || isEditing ? (
    <LegacyCard.Ant className="tw:w-full" styles={{ body: { padding: "16px" } }}>
      <Flex justify="space-between" gap="16px" className="tw:w-full">
        <Flex vertical gap="16px" className="tw:flex-1">
          {isEditing ? (
            <EditCardContent
              iban={iban}
              status={status}
              hasPending={hasPending}
              onDelete={onDelete}
              isActiveIBAN={isActiveIBAN}
              onActivate={onActivate}
              fieldData={fieldData}
            />
          ) : (
            <DisplayCardContent
              iban={iban || ""}
              isActiveIBAN={isActiveIBAN}
              errorMessage={errorMessage}
              title={title}
              status={status}
              onActivate={onActivate}
            />
          )}

          {status !== MerchantIbanStatusEnum.Accepted || !isActiveIBAN ? null : (
            <Alert
              type="warning"
              title={t("profile.bank.activeIBANSettlementWarning")}
              showIcon={false}
              className="tw:mt-2!"
            />
          )}
        </Flex>
      </Flex>
    </LegacyCard.Ant>
  ) : null;
};

export default ShebaCard;
